/**
 * Price Charts Page (Settings)
 * List, create, edit, activate/deactivate and delete the tenant's price
 * charts: size bands that price chart-linked items from a property's size.
 *
 * A band is defined only by its upper bound ("up to"); its lower bound is
 * the previous band's, so bands never gap or overlap. Sizes above the last
 * band are priced with the overage rate. Prices are computed only by the
 * backend; this page edits the chart, it does not calculate prices (a
 * "try a size" preview is deferred).
 *
 * ADMIN and OFFICE_MANAGER may change charts (backend
 * requireRole('ADMIN', 'OFFICE_MANAGER')); other roles get a read-only view.
 * Saving an existing chart PATCHes its fields, then PUTs the whole band set.
 * Delete is refused (409 PRICE_CHART_IN_USE) while items link to the chart or
 * job lines were priced from it.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, IPriceChartsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { canManagePriceCharts } from '../../utils/permissions';
import {
  EMPTY_PRICE_CHART_FORM,
  PRICE_CHART_DESCRIPTION_MAX_LENGTH,
  PRICE_CHART_NAME_MAX_LENGTH,
  PRICING_TYPE_HELP,
  PRICING_TYPE_LABELS,
  PRICING_TYPE_OPTIONS,
  PriceChartFormState,
  SIZE_DIMENSION_LABELS,
  SIZE_UNIT_OPTIONS,
  TierRow,
  buildCreatePriceChartRequest,
  buildUpdatePriceChartRequest,
  formatRate,
  formatSizeNumber,
  impliedLowerBounds,
  newTierRow,
  overageExplanation,
  priceChartFormFromChart,
  sizeUnitLabel,
  tierRowsFromTiers,
  tierRowsToInput,
  tiersChanged,
  validatePriceChartForm,
  validateTierRows,
} from '../../utils/priceChart';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
} from '../../components/formStyles';
import { StarterMark, useStarterRecords } from '../../components/StarterBadge';
import { Toast } from '../../components/Toast';

type Editing =
  | { mode: 'create' }
  | { mode: 'edit'; chart: types.PriceChart }
  | { mode: 'view'; chart: types.PriceChart }
  | null;

const thClass = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
/** Taller row-action buttons on phones (44px touch target); unchanged from sm up. */
const tapClass = 'min-h-[44px] sm:min-h-0';

/** The price column's meaning for a row, which depends on the pricing type. */
function priceLabel(pricingType: types.PriceChartPricingType, index: number, unitLabel: string): string {
  if (pricingType === 'BAND_FLAT') return 'Price';
  return index === 0 ? 'Minimum charge' : `Rate per ${unitLabel}`;
}

function lowerBoundText(bound: string): string {
  return bound === '?' || !Number.isFinite(Number(bound)) ? 'over ?' : `over ${formatSizeNumber(bound)}`;
}

export function PriceChartsPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<IPriceChartsApi>(() => createAPIClient(), []);
  const canManage = canManagePriceCharts(user);
  const starter = useStarterRecords('PRICE_CHART');

  // State
  const [charts, setCharts] = useState<types.PriceChart[]>([]);
  const [units, setUnits] = useState<types.SizeUnitOption[]>(SIZE_UNIT_OPTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [form, setForm] = useState<PriceChartFormState>(EMPTY_PRICE_CHART_FORM);
  const [rows, setRows] = useState<TierRow[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const refreshCharts = useCallback(async () => {
    setCharts(await apiClient.listPriceCharts());
  }, [apiClient]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // The unit list is fixed; fall back to the built-in copy if it fails.
        const [chartsResult, unitsResult] = await Promise.allSettled([
          apiClient.listPriceCharts(),
          apiClient.listSizeUnits(),
        ]);
        if (chartsResult.status === 'rejected') {
          throw chartsResult.reason;
        }
        setCharts(chartsResult.value);
        if (unitsResult.status === 'fulfilled' && unitsResult.value.length > 0) {
          setUnits(unitsResult.value);
        }
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load price charts'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [apiClient]);

  const readOnly = !canManage || editing?.mode === 'view';
  const editingChart = editing && editing.mode !== 'create' ? editing.chart : null;
  const unitText = sizeUnitLabel(form.unit, units);
  const tierValidation = validateTierRows(rows);
  const bounds = impliedLowerBounds(rows);
  const unitGroups = (['AREA', 'LENGTH'] as const).map((dimension) => ({
    dimension,
    units: units.filter((u) => u.dimension === dimension),
  }));

  const setField = <K extends keyof PriceChartFormState>(field: K, value: PriceChartFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateRow = (key: string, patch: Partial<TierRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const resetFormState = () => {
    setFormError(null);
    setSubmitted(false);
    setConfirmDeleteId(null);
  };

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(EMPTY_PRICE_CHART_FORM);
    setRows([newTierRow()]);
    resetFormState();
  };

  /** Load the chart with its bands (the list has none), then open it. */
  const openChart = async (chart: types.PriceChart, mode: 'edit' | 'view') => {
    try {
      setOpeningId(chart.id);
      setError(null);
      const full = await apiClient.getPriceChart(chart.id);
      setEditing({ mode, chart: full });
      setForm(priceChartFormFromChart(full));
      setRows(tierRowsFromTiers(full.tiers));
      resetFormState();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load price chart'));
    } finally {
      setOpeningId(null);
    }
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_PRICE_CHART_FORM);
    setRows([]);
    resetFormState();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing || readOnly) return;
    setSubmitted(true);

    const fieldError = validatePriceChartForm(form);
    if (fieldError) {
      setFormError(fieldError);
      return;
    }
    if (!tierValidation.valid) {
      setFormError(tierValidation.setError ?? 'Fix the highlighted bands');
      return;
    }

    try {
      setIsSaving(true);
      setFormError(null);

      if (editing.mode === 'edit') {
        const chart = editing.chart;
        const patch = buildUpdatePriceChartRequest(form, chart);
        if (Object.keys(patch).length > 0) {
          await apiClient.updatePriceChart(chart.id, patch);
        }
        if (tiersChanged(rows, chart.tiers)) {
          try {
            await apiClient.replacePriceChartTiers(chart.id, tierRowsToInput(rows));
          } catch (err) {
            // The fields may have saved while the bands did not. Re-read the
            // chart so the next save diffs against what is stored, and keep
            // the typed bands on screen for the user to fix.
            const message = getErrorMessage(err, 'Failed to save bands');
            try {
              const fresh = await apiClient.getPriceChart(chart.id);
              setEditing({ mode: 'edit', chart: fresh });
              setForm(priceChartFormFromChart(fresh));
            } catch {
              // Keep the old baseline; the next save re-sends the fields.
            }
            setFormError(
              Object.keys(patch).length > 0 ? `Chart details were saved, but the bands were not: ${message}` : message
            );
            await refreshCharts().catch(() => undefined);
            return;
          }
        }
      } else {
        await apiClient.createPriceChart(buildCreatePriceChartRequest(form, rows));
      }

      closeForm();
      await refreshCharts();
    } catch (err) {
      // e.g. 409 DUPLICATE_PRICE_CHART
      setFormError(
        getErrorMessage(err, editing.mode === 'edit' ? 'Failed to update price chart' : 'Failed to create price chart')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (chart: types.PriceChart) => {
    try {
      setTogglingId(chart.id);
      setError(null);
      await apiClient.updatePriceChart(chart.id, { active: !chart.active });
      await refreshCharts();
    } catch (err) {
      setError(getErrorMessage(err, chart.active ? 'Failed to deactivate price chart' : 'Failed to activate price chart'));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (chart: types.PriceChart) => {
    try {
      setDeletingId(chart.id);
      setError(null);

      await apiClient.deletePriceChart(chart.id);
      setConfirmDeleteId(null);
      if (editingChart?.id === chart.id) closeForm();
      await refreshCharts();
    } catch (err) {
      // e.g. 409 PRICE_CHART_IN_USE "Cannot delete a price chart that items are linked to..."
      setConfirmDeleteId(null);
      setError(getErrorMessage(err, 'Failed to delete price chart'));
    } finally {
      setDeletingId(null);
    }
  };

  const showRowError = (row: TierRow, index: number): string | null => {
    const rowError = tierValidation.rowErrors[index];
    if (!rowError) return null;
    // Live validation for rows the user has typed in; every row after a save attempt.
    return submitted || row.upToSize.trim() !== '' || row.price.trim() !== '' ? rowError : null;
  };

  const typeOrUnitChanged =
    editing?.mode === 'edit' && (form.unit !== editing.chart.unit || form.pricingType !== editing.chart.pricingType);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div className="min-w-0">
          <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Price Charts</h1>
          <p className="mt-1 text-sm text-gray-600">
            Price services from a property's size. Link a chart to an item on the{' '}
            <Link to="/settings/items" className="font-semibold text-blue-600 hover:text-blue-700">
              Items
            </Link>{' '}
            page.
          </p>
        </div>
        {canManage && !editing && (
          <button onClick={openCreate} className={`${primaryButtonClass} w-full sm:w-auto`}>
            Add Price Chart
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Add / Edit / View Form */}
      {editing && (
        <section className="bg-white rounded-lg shadow mb-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6 space-y-6">
              <h2 className="text-base font-semibold text-gray-900 break-words">
                {editing.mode === 'create'
                  ? 'New Price Chart'
                  : editing.mode === 'edit'
                    ? `Edit ${editing.chart.name}`
                    : editing.chart.name}
              </h2>

              {formError && <Toast kind="error">{formError}</Toast>}

              {editingChart && editingChart.inUse && !readOnly && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-900">
                  {editingChart.itemCount} item{editingChart.itemCount === 1 ? '' : 's'} and {editingChart.jobLineCount} job
                  line{editingChart.jobLineCount === 1 ? '' : 's'} use this chart. Changes apply to job lines priced from now on
                  (including generated recurring jobs); lines already saved keep their price.
                  {typeOrUnitChanged && ' You are changing the unit or pricing type: check that the bands still make sense.'}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="chartName" className={labelClass}>
                    Name<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="chartName"
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    disabled={isSaving || readOnly}
                    maxLength={PRICE_CHART_NAME_MAX_LENGTH}
                    placeholder="Lawn Area"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-3 flex items-end">
                  <label htmlFor="chartActive" className="inline-flex flex-wrap items-center gap-2 py-2.5 text-sm text-gray-700">
                    <input
                      id="chartActive"
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setField('active', e.target.checked)}
                      disabled={isSaving || readOnly}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-medium">Active</span>
                    <span className="text-xs text-gray-500">(inactive charts cannot price job lines or be linked to items)</span>
                  </label>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="chartDescription" className={labelClass}>
                    Description
                  </label>
                  <input
                    id="chartDescription"
                    type="text"
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                    disabled={isSaving || readOnly}
                    maxLength={PRICE_CHART_DESCRIPTION_MAX_LENGTH}
                    placeholder="Optional"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="chartUnit" className={labelClass}>
                    Unit<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="chartUnit"
                    value={form.unit}
                    onChange={(e) => setField('unit', e.target.value as types.SizeUnit)}
                    disabled={isSaving || readOnly}
                    className={inputClass}
                  >
                    {unitGroups.map((group) => (
                      <optgroup key={group.dimension} label={SIZE_DIMENSION_LABELS[group.dimension]}>
                        {group.units.map((u) => (
                          <option key={u.unit} value={u.unit}>
                            {u.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-gray-500">
                    Property sizes in another unit of the same kind (area or length) are converted.
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="chartPricingType" className={labelClass}>
                    Pricing type<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="chartPricingType"
                    value={form.pricingType}
                    onChange={(e) => setField('pricingType', e.target.value as types.PriceChartPricingType)}
                    disabled={isSaving || readOnly}
                    className={inputClass}
                  >
                    {PRICING_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {PRICING_TYPE_LABELS[option]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="chartOverage" className={labelClass}>
                    Overage rate per {unitText}<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500">$</span>
                    <input
                      id="chartOverage"
                      type="text"
                      inputMode="decimal"
                      value={form.overageRatePerUnit}
                      onChange={(e) => setField('overageRatePerUnit', e.target.value)}
                      disabled={isSaving || readOnly}
                      placeholder="0.005"
                      aria-describedby="chartOverageHint"
                      className={`${inputClass} pl-7`}
                    />
                  </div>
                  <p id="chartOverageHint" className="mt-1.5 text-xs text-gray-500">
                    {overageExplanation(rows, form.unit, form.pricingType)} Up to 4 decimal places; 0 for none.
                  </p>
                </div>
              </div>

              {/* Bands */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Bands</h3>
                <p className="mt-1 text-sm text-gray-600">{PRICING_TYPE_HELP[form.pricingType]}</p>
                <p className="mt-1 text-xs text-gray-500">
                  Each band covers sizes above the previous band's bound, up to and including its own. The first band starts at
                  0; the last band's bound is the chart maximum.
                </p>

                {tierValidation.setError && (submitted || rows.length > 0) && (
                  <p role="alert" className="mt-3 text-sm text-red-700">
                    {tierValidation.setError}
                  </p>
                )}

                <div className="mt-3 overflow-x-auto">
                  <table className="responsive-table min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={thClass}>Band</th>
                        <th className={thClass}>From</th>
                        <th className={thClass}>Up to ({unitText})</th>
                        <th className={thClass}>Price</th>
                        {!readOnly && (
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <span className="sr-only">Actions</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {rows.map((row, index) => {
                        const rowError = showRowError(row, index);
                        const label = priceLabel(form.pricingType, index, unitText);
                        return (
                          <React.Fragment key={row.key}>
                            <tr>
                              <td data-label="Band" className="px-4 py-2 text-sm text-gray-600">{index + 1}</td>
                              <td data-label="From" className="px-4 py-2 text-sm text-gray-600 whitespace-nowrap">{lowerBoundText(bounds[index])}</td>
                              <td data-label={`Up to (${unitText})`} className="px-4 py-2">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  aria-label={`Band ${index + 1} up to`}
                                  aria-invalid={rowError ? true : undefined}
                                  value={row.upToSize}
                                  onChange={(e) => updateRow(row.key, { upToSize: e.target.value })}
                                  disabled={isSaving || readOnly}
                                  placeholder="5000"
                                  className={`${inputClass} min-w-0 flex-1`}
                                />
                              </td>
                              <td data-label="Price" className="px-4 py-2">
                                <div className="min-w-0 flex-1">
                                  <div className="relative">
                                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-500">
                                      $
                                    </span>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      aria-label={`Band ${index + 1} ${label}`}
                                      aria-invalid={rowError ? true : undefined}
                                      value={row.price}
                                      onChange={(e) => updateRow(row.key, { price: e.target.value })}
                                      disabled={isSaving || readOnly}
                                      placeholder={form.pricingType === 'RATE_PER_UNIT' && index > 0 ? '0.01' : '50.00'}
                                      className={`${inputClass} pl-7`}
                                    />
                                  </div>
                                  <p className="mt-1 text-xs text-gray-500">{label}</p>
                                </div>
                              </td>
                              {!readOnly && (
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                  <button
                                    type="button"
                                    onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                                    disabled={isSaving}
                                    aria-label={`Remove band ${index + 1}`}
                                    className={`${rowDangerActionClass} ${tapClass}`}
                                  >
                                    Remove
                                  </button>
                                </td>
                              )}
                            </tr>
                            {rowError && (
                              <tr>
                                <td colSpan={readOnly ? 4 : 5} className="px-4 pb-2 text-sm text-red-700">
                                  Band {index + 1}: {rowError}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => setRows((prev) => [...prev, newTierRow()])}
                    disabled={isSaving}
                    className={`${secondaryButtonClass} mt-3 w-full sm:w-auto`}
                  >
                    Add band
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={isSaving} className={secondaryButtonClass}>
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {!readOnly && (
                <button type="submit" disabled={isSaving} className={primaryButtonClass}>
                  {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save Changes' : 'Create Price Chart'}
                </button>
              )}
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Table */}
        {!isLoading &&
          (charts.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No price charts yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>Name</th>
                    <th className={thClass}>Unit</th>
                    <th className={thClass}>Pricing</th>
                    <th className={thClass}>Bands</th>
                    <th className={thClass}>Overage</th>
                    <th className={thClass}>Items</th>
                    <th className={thClass}>Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {charts.map((chart) => {
                    const isConfirming = confirmDeleteId === chart.id;
                    const isDeleting = deletingId === chart.id;
                    const busy = isSaving || openingId !== null || togglingId !== null || deletingId !== null;
                    return (
                      <tr key={chart.id}>
                        <td data-label="Name" data-primary className="px-4 py-3">
                          <div className="min-w-0 break-words">
                            <div className="font-semibold text-gray-900">{chart.name}</div>
                            <StarterMark records={starter} entityId={chart.id} name={chart.name} />
                            {chart.description && <div className="text-sm font-normal text-gray-500">{chart.description}</div>}
                          </div>
                        </td>
                        <td data-label="Unit" className="px-4 py-3 text-sm text-gray-900">{sizeUnitLabel(chart.unit, units)}</td>
                        <td data-label="Pricing" className="px-4 py-3 text-sm text-gray-900">{PRICING_TYPE_LABELS[chart.pricingType] ?? chart.pricingType}</td>
                        <td data-label="Bands" className="px-4 py-3 text-sm text-gray-600">{chart.tierCount}</td>
                        <td data-label="Overage" className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {formatRate(chart.overageRatePerUnit)} / {sizeUnitLabel(chart.unit, units)}
                        </td>
                        <td data-label="Items" className="px-4 py-3 text-sm text-gray-600">{chart.itemCount}</td>
                        <td data-label="Status" className="px-4 py-3 text-sm">
                          {chart.active ? (
                            <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-800">Active</span>
                          ) : (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-normal sm:whitespace-nowrap">
                          {isConfirming ? (
                            <span className="inline-flex flex-wrap items-center gap-x-3 sm:gap-3">
                              <span className="text-sm text-gray-700">Delete this price chart?</span>
                              <button onClick={() => handleDelete(chart)} disabled={isDeleting} className={`${rowDangerActionClass} ${tapClass}`}>
                                {isDeleting ? 'Deleting...' : 'Confirm'}
                              </button>
                              <button onClick={() => setConfirmDeleteId(null)} disabled={isDeleting} className={`${rowActionClass} ${tapClass}`}>
                                Cancel
                              </button>
                            </span>
                          ) : canManage ? (
                            <span className="inline-flex flex-wrap items-center gap-x-4 sm:gap-4">
                              <button
                                onClick={() => openChart(chart, 'edit')}
                                disabled={busy}
                                className={`${rowActionClass} ${tapClass}`}
                              >
                                {openingId === chart.id ? 'Opening...' : 'Edit'}
                              </button>
                              <button
                                onClick={() => handleToggleActive(chart)}
                                disabled={busy}
                                className={`${rowActionClass} ${tapClass}`}
                              >
                                {togglingId === chart.id ? 'Saving...' : chart.active ? 'Deactivate' : 'Activate'}
                              </button>
                              <span
                                title={
                                  chart.inUse
                                    ? 'In use by items or job lines: unlink the items (or deactivate the chart) instead'
                                    : undefined
                                }
                              >
                                <button
                                  onClick={() => setConfirmDeleteId(chart.id)}
                                  disabled={busy}
                                  aria-label={`Delete ${chart.name}`}
                                  className={`${rowDangerActionClass} ${tapClass}`}
                                >
                                  Delete
                                </button>
                              </span>
                            </span>
                          ) : (
                            <button
                              onClick={() => openChart(chart, 'view')}
                              disabled={openingId !== null}
                              className={`${rowActionClass} ${tapClass}`}
                            >
                              {openingId === chart.id ? 'Opening...' : 'View bands'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>
    </main>
  );
}
